import React, { useEffect } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { useFormContext } from 'react-hook-form';

// redux
import { NQuizQuestion, QuizQuestionType } from 'redux/schemas/models/quiz';
import { useSelector } from 'react-redux';
import { getFlatCourseAliases } from 'redux/selectors/course';

import QuestionSetContext, { QuestionContext } from 'lecture_pages/services/question-set-context';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import
{ danger,
  gray2,
  gray3,
  gray4,
  gray5,
  primary,
  success,
  white,
} from 'styles/global_defaults/colors';
import { quarterSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import AnswerFeedback from 'quizzes/components/feedback-components/answer-feedback';
import ViewAnswerFeedBack from 'quizzes/components/feedback-components/view-answer-feedback';
import { removeDecimalForInteger } from 'quizzes/components/utils';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import NumberAnswerSettingContainer from './number-answer-setting-container';

type Limits = {
  maxLimit: number;
  minLimit: number;
};

type QuizQuestionProps = {
  currentQuestion: NQuizQuestion;
  limits?: Limits
};

const NumberAnswerSection = (props: QuizQuestionProps) => {
  const { currentQuestion, limits } = props;
  const {
    answerState,
    setAnswerState,
    responseOptions,
  } = React.useContext(QuestionContext);

  const {
    currentQuestionResponse,
    isHardDeadlineExpired,
    isContentManagementCollection,
  } = React.useContext(QuestionSetContext);

  const { formState, setValue, trigger } = useFormContext() || {};
  const errors = formState?.errors;

  const courseAliases = useSelector(getFlatCourseAliases);

  const {
    isEditMode,
    isAnswerMode,
    isReviewMode,
    showAnswerFeedback,
    getQuestionLabelElementId,
    getFeedbackElementId,
    isCurrentActivityType,
    getAnswerInputBackgroundColor,
  } = useQuestionSetHelpers();

  const [correctAnswerFeedback, setCorrectAnswerFeedback] = React.useState(responseOptions[0]?.explanation);
  const [incorrectAnswerFeedback, setIncorrectAnswerFeedback] = React.useState(responseOptions[0]?.secondaryExplanation);
  const isTopRangePresent = !!responseOptions[0]?.topOfRange
    && (responseOptions[0].topOfRange !== responseOptions[0]?.bottomOfRange);

  const [isAnswerInRange, setIsAnswerInRange] = React.useState(false);
  const [bottomOfRange, setBottomOfRange] = React.useState(removeDecimalForInteger(responseOptions[0]?.bottomOfRange) || '');
  const [topOfRange, setTopOfRange] = React.useState(isTopRangePresent ? removeDecimalForInteger(responseOptions[0]?.topOfRange) : '');
  const [triggerShowFeedback, setTriggerShowFeedback] = React.useState(false);

  const showFeedbackIcon = !(correctAnswerFeedback !== null && incorrectAnswerFeedback !== null);

  useEffect(() => {
    if (isCurrentActivityType(ComponentType.QUIZ) || isCurrentActivityType(ComponentType.SURVEY)) {
      if (currentQuestionResponse) {
        setValue(currentQuestion.id.toString(), currentQuestionResponse.response);
      }
    }
  }, [
    currentQuestion.displayIndex,
    currentQuestion.id,
    currentQuestionResponse,
    isCurrentActivityType,
    setValue,
  ]);

  useEffect(() => {
    if (isTopRangePresent) {
      setIsAnswerInRange(true);
    }
  }, [isTopRangePresent]);

  const getBorderColor = () => {
    if (currentQuestionResponse) {
      if (isCurrentActivityType(ComponentType.SURVEY)) {
        return gray3;
      }
      if (currentQuestionResponse.isCorrect) {
        return success;
      }
      return danger;
    }

    if (errors && errors[currentQuestion.id]) {
      return danger;
    }
    return gray3;
  };

  const styles = css`
    .input-style {
      font-size: 16px;
      border-color: ${getBorderColor()} !important;
      border-radius: ${quarterSpacing}px;
      background-color: ${getAnswerInputBackgroundColor()} !important;

      ::placeholder {
        color: ${gray2};
      }
    }
    .answer-input-group {
      .bs4-input-group-text {
        background: ${getAnswerInputBackgroundColor()} !important;
        border-top-right-radius: ${quarterSpacing}px;
        border-bottom-right-radius: ${quarterSpacing}px;
        border-color: ${getBorderColor()} !important;
      }
    }
    ${isAnswerMode && !currentQuestionResponse && css`
        .input-style:focus {
          border-color: ${primary} !important;
        }
        .answer-input-group:focus-within {
          .bs4-input-group-text {
            border-color: ${primary} !important;
          }
        }
    `}
    .input-style {
      font-size: 16px;
      border-radius: ${quarterSpacing}px;
      background-color: ${white};

      ::placeholder {
        color: ${gray2};
      }
    }
    .number-setting-container {
      height: ${tripleSpacing}px;
      .dash-component {
        width: 10%;
      }
    }
    .disabled-number-input {
      input {
        ::placeholder {
          color: ${gray4};
        }
      }
    }
  `;

  const getValue = () => {
    if (currentQuestionResponse) {
      return currentQuestionResponse.response;
    }
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      if (answerState === null) {
        return '';
      }
      return answerState?.toString();
    }
    return null;
  };

  const handleAnswerChange = (e) => {
    e.persist();
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      setAnswerState(e.target.value);
    } else {
      setValue(currentQuestion.id.toString(), e?.target?.value);
      trigger(currentQuestion.id.toString());
    }
  };

  const isDisabled = !isAnswerMode
   || !!currentQuestionResponse
   || isHardDeadlineExpired
   || isContentManagementCollection;

  const getPlaceHolder = () => {
    if (isCurrentActivityType(ComponentType.QUIZ)) {
      if (currentQuestionResponse) {
        return '';
      }
      return '0';
    }
    if (isReviewMode && !getValue()) {
      return '';
    }
    return '0';
  };

  const getEditModeContent = () => {
    if (isCurrentActivityType(ComponentType.SURVEY)) {
      if (currentQuestion.type === QuizQuestionType.NUMBER_QUESTION) {
        // This disabled input is just to show how it will look in the survey learner view for number answer question
        return (
          <NvTextInput
            className='w-50 disabled-number-input'
            inputClassName='py-5 pl-2 w-100 input-style bg-transparent'
            disabled
            placeholder='0'
          />
        );
      }
      return null;
    }
    return (
      <React.Fragment>
        <div className='d-flex pt-5'>
          <p className='semi-bold mb-0'>{t.QUIZZES.NUMBER_ANSWER_QUESTION.CORRECT_NUMBER()}: </p>
          <NvPopover
            className='d-flex'
            showOnHover
            placement='top'
            content={(
              <div className=''>
                {t.QUIZZES.NUMBER_ANSWER_QUESTION.SETTING_NUMBER_ANSWER_INFO(courseAliases)}
              </div>
              )}
          >
            <NvIcon
              className='d-flex align-items-center'
              icon='info ml-1'
              size='xs-smallest text-primary'
            />
          </NvPopover>
        </div>
        <NumberAnswerSettingContainer
          setTriggerShowFeedback={setTriggerShowFeedback}
          isAnswerInRange={isAnswerInRange}
          setIsAnswerInRange={setIsAnswerInRange}
          topOfRange={topOfRange}
          bottomOfRange={bottomOfRange}
          setBottomOfRange={setBottomOfRange}
          setTopOfRange={setTopOfRange}
          showFeedbackIcon={showFeedbackIcon}
          limits={limits}
        />
        <AnswerFeedback
          currentQuestion={currentQuestion}
          correctAnswerFeedback={correctAnswerFeedback}
          incorrectAnswerFeedback={incorrectAnswerFeedback}
          setCorrectAnswerFeedback={setCorrectAnswerFeedback}
          setIncorrectAnswerFeedback={setIncorrectAnswerFeedback}
          triggerShowFeedback={triggerShowFeedback}
          setTriggerShowFeedback={setTriggerShowFeedback}
        />
      </React.Fragment>
    );
  };

  return (
    <div css={styles}>
      <div className={`${isCurrentActivityType(ComponentType.SURVEY) ? 'w-50' : 'w-100'} mb-5`}>
        {!isEditMode && (
          <div className='mx-auto'>
            <NvTextInput
              inputClassName='py-5 pl-2 w-100 input-style'
              className='answer-input-group'
              readOnly={isDisabled}
              placeholder={getPlaceHolder()}
              value={getValue() as string}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              type='number'
              name={currentQuestion.id.toString()}
              errorValidationPopoverTitle={t.VALIDATION.ERROR()}
              withForm={isCurrentActivityType(ComponentType.QUIZ) || isCurrentActivityType(ComponentType.SURVEY)}
              onChange={handleAnswerChange}
              showErrorWithoutFocus
              aria-labelledby={getQuestionLabelElementId(currentQuestion.id)}
              ariaDescribedby={getFeedbackElementId(currentQuestion.id)}
              required={currentQuestion.isRequired && (isCurrentActivityType(ComponentType.SURVEY))}
            />
          </div>
        )}
        {currentQuestionResponse && showAnswerFeedback && (
          <ViewAnswerFeedBack currentQuestion={currentQuestion} />
        )}
      </div>
      {isEditMode && (
        getEditModeContent()
      )}
    </div>
  );
};

export default NumberAnswerSection;
